/**
 * 前端 Excel 解析：SheetJS 讀 .xls/.xlsx → 二維陣列 → 複用 @bom/shared 的 parseBomSheet。
 * 後端零 Excel 處理（excel-import Lambda 已移除）。
 */

import * as XLSX from 'xlsx';
import {
  parseBomFlatSheet,
  parseEcnSheet,
  PASSWORD_MIN_LENGTH,
  type BomParseResult,
  type EcnParseResult,
} from '@bom/shared';

export type { BomParseResult, EcnParseResult };

/** v2：扁平 BOM 解析（S1 Ultra.xls 格式，不建树） */
export function parseBomFlat(wb: XLSX.WorkBook, sheetName: string): BomParseResult {
  return parseBomFlatSheet(sheetToRows(wb, sheetName));
}

/** v2：ECN 解析（ECN20260122B.xls 格式：表头 + 变更前后明细） */
export function parseEcn(wb: XLSX.WorkBook, sheetName: string): EcnParseResult {
  return parseEcnSheet(sheetToRows(wb, sheetName));
}

/** 讀檔成 workbook（每個 sheet 對應一個 BOM） */
export async function readWorkbook(file: File): Promise<XLSX.WorkBook> {
  const buf = await file.arrayBuffer();
  return XLSX.read(buf, { type: 'array' });
}

/** 某 sheet → 二維陣列（header:1 表示回傳陣列而非物件；defval:null 補空格） */
export function sheetToRows(wb: XLSX.WorkBook, sheetName: string): unknown[][] {
  const ws = wb.Sheets[sheetName];
  if (!ws) return [];
  return XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: null }) as unknown[][];
}

/** 导出当前扁平 BOM 为 .xlsx（还原原始列：映射字段 + extra 里保留的列），浏览器直接存档 */
const BOM_EXPORT_HEADER = [
  '层级',
  '归属BOM',
  '子BOMID',
  '母件编号',
  'BOM序号',
  '子件编号',
  '物料名称',
  '规格',
  '单位',
  '单位用量',
  '位号',
  '状态',
  'ECN编号',
  'BOM备注',
  '存在替代',
  '是否替代料',
  '损耗率',
  '提前期',
  '最小订购量',
  '库存量',
  '承认阶段',
  'BOM等级',
  '工作中心',
  '是否跳层',
];
export function exportBomRowsToXlsx(rows: readonly unknown[], fileName: string): void {
  const data = rows.map((row) => {
    const r = row as Record<string, unknown>;
    const extra = (r['extra'] ?? {}) as Record<string, unknown>;
    return {
      层级: r['level_label'] ?? '',
      子件编号: r['part_no'] ?? '',
      物料名称: r['name'] ?? '',
      规格: r['spec'] ?? '',
      单位: r['unit'] ?? '',
      单位用量: r['qty'] ?? '',
      位号: r['designator'] ?? '',
      状态: r['change_status'] ?? '',
      ECN编号: r['ecn_no'] ?? '',
      BOM备注: r['note'] ?? '',
      ...extra, // 归属BOM/子BOMID/母件编号/BOM序号/库存量… 原样带回
    };
  });
  const ws = XLSX.utils.json_to_sheet(data, { header: BOM_EXPORT_HEADER });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'BOM');
  XLSX.writeFile(wb, fileName);
}

/** 下載 .xlsx 模板（表頭 + 可選範例列），瀏覽器直接存檔 */
export function downloadTemplate(
  filename: string,
  headers: string[],
  sample: string[][] = [],
): void {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...sample]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

// ── 通用：按表头(第一行)映射为对象列 ──
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
/**
 * 归一化表头：去掉括号说明（全角/半角）、必填星号、空格后再小写。
 * 因此模板表头可写成「成员角色（管理员/编辑/审核员）」「邮箱*」，导入仍能正确识别。
 */
const normHeader = (s: unknown): string =>
  String(s ?? '')
    .replace(/（[^）]*）/g, '')
    .replace(/\([^)]*\)/g, '')
    .replace(/[*＊\s]/g, '')
    .toLowerCase();

/**
 * 找到含锚点列的表头行，把每个数据行映射为 {field: value}（附 __row 1-based 行号）。
 * anchor = { field, label }：用某个映射后字段(如 email/code)判定表头行所在，label 用于报错文案。
 */
function parseKeyedSheet(
  wb: XLSX.WorkBook,
  sheetName: string,
  fieldMap: Record<string, string>,
  anchor: { field: string; label: string } = { field: 'email', label: '邮箱' },
): { items: Record<string, string>[]; errors: { rowIndex: number; reason: string }[] } {
  const rows = sheetToRows(wb, sheetName);
  const errors: { rowIndex: number; reason: string }[] = [];
  let headerIdx = -1;
  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const cells = (rows[i] ?? []).map(normHeader);
    if (cells.some((c) => fieldMap[c] === anchor.field)) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx < 0) {
    errors.push({ rowIndex: -1, reason: `找不到表头（需含「${anchor.label}」列）` });
    return { items: [], errors };
  }
  const headerRow = rows[headerIdx] ?? [];
  const colMap = new Map<number, string>();
  headerRow.forEach((raw, idx) => {
    const f = fieldMap[normHeader(raw)];
    if (f) colMap.set(idx, f);
  });
  const items: Record<string, string>[] = [];
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i] ?? [];
    if (row.every((c) => c == null || String(c).trim() === '')) continue;
    const obj: Record<string, string> = { __row: String(i + 1) };
    colMap.forEach((field, idx) => {
      const raw = row[idx];
      if (raw != null && String(raw).trim() !== '') obj[field] = String(raw).trim();
    });
    items.push(obj);
  }
  return { items, errors };
}

// ── 联系人（产品 / 分组）批量导入 ──
const CONTACT_IMPORT_FIELD_MAP: Record<string, string> = {
  邮箱: 'email',
  邮件: 'email',
  email: 'email',
  'e-mail': 'email',
  姓名: 'name',
  名称: 'name',
  名字: 'name',
  name: 'name',
  角色标签: 'role_label',
  角色: 'role_label',
  标签: 'role_label',
  role: 'role_label',
  单位: 'org',
  公司: 'org',
  org: 'org',
  备注: 'note',
  remark: 'note',
  note: 'note',
};

export interface ContactImportRow {
  email: string;
  name: string;
  role_label?: string;
  org?: string;
  note?: string;
}

/** 解析联系人导入表；requireOrg=true 时「单位」必填（产品联系人需要，分组联系人不需要） */
export function parseContactSheet(
  wb: XLSX.WorkBook,
  sheetName: string,
  requireOrg = false,
): { items: ContactImportRow[]; errors: { rowIndex: number; reason: string }[] } {
  const { items, errors } = parseKeyedSheet(wb, sheetName, CONTACT_IMPORT_FIELD_MAP);
  const out: ContactImportRow[] = [];
  for (const it of items) {
    const rowNo = Number(it['__row']);
    const email = (it['email'] ?? '').toLowerCase();
    if (!EMAIL_RE.test(email)) {
      errors.push({ rowIndex: rowNo, reason: `邮箱无效：${it['email'] ?? '(空)'}` });
      continue;
    }
    if (!it['name']) {
      errors.push({ rowIndex: rowNo, reason: `缺少姓名（${email}）` });
      continue;
    }
    if (requireOrg && !it['org']) {
      errors.push({ rowIndex: rowNo, reason: `缺少单位（${email}）` });
      continue;
    }
    out.push({
      email,
      name: it['name'],
      ...(it['role_label'] ? { role_label: it['role_label'] } : {}),
      ...(it['org'] ? { org: it['org'] } : {}),
      ...(it['note'] ? { note: it['note'] } : {}),
    });
  }
  return { items: out, errors };
}

export const CONTACT_TEMPLATE_HEADERS = [
  '邮箱*',
  '姓名*',
  '角色标签（选填）',
  '单位*',
  '备注（选填）',
];
export const CONTACT_TEMPLATE_SAMPLE = [['zhang@abc.com', '张三', '采购', '甲方', '']];
export const GROUP_CONTACT_TEMPLATE_HEADERS = [
  '邮箱*',
  '姓名*',
  '角色标签（选填）',
  '备注（选填）',
];
export const GROUP_CONTACT_TEMPLATE_SAMPLE = [['li@abc.com', '李四', '品管', '']];

// ── 联系人分组「整表导入」：一张表含 分组名/公司 + 联系人，按需自动建组 ──
const GROUP_FULL_IMPORT_FIELD_MAP: Record<string, string> = {
  分组名: 'group_name',
  分组: 'group_name',
  分組名: 'group_name',
  group: 'group_name',
  公司: 'company',
  company: 'company',
  邮箱: 'email',
  邮件: 'email',
  email: 'email',
  姓名: 'name',
  名称: 'name',
  name: 'name',
  角色标签: 'role_label',
  角色: 'role_label',
  role: 'role_label',
  备注: 'note',
  note: 'note',
};

export interface GroupFullImportRow {
  group_name: string;
  company: string;
  email: string;
  name: string;
  role_label?: string;
  note?: string;
}

export function parseGroupFullSheet(
  wb: XLSX.WorkBook,
  sheetName: string,
): { items: GroupFullImportRow[]; errors: { rowIndex: number; reason: string }[] } {
  const { items, errors } = parseKeyedSheet(wb, sheetName, GROUP_FULL_IMPORT_FIELD_MAP);
  const out: GroupFullImportRow[] = [];
  for (const it of items) {
    const rowNo = Number(it['__row']);
    if (!it['group_name'] || !it['company']) {
      errors.push({ rowIndex: rowNo, reason: '缺少分组名或公司' });
      continue;
    }
    const email = (it['email'] ?? '').toLowerCase();
    if (!EMAIL_RE.test(email)) {
      errors.push({ rowIndex: rowNo, reason: `邮箱无效：${it['email'] ?? '(空)'}` });
      continue;
    }
    if (!it['name']) {
      errors.push({ rowIndex: rowNo, reason: `缺少姓名（${email}）` });
      continue;
    }
    out.push({
      group_name: it['group_name'],
      company: it['company'],
      email,
      name: it['name'],
      ...(it['role_label'] ? { role_label: it['role_label'] } : {}),
      ...(it['note'] ? { note: it['note'] } : {}),
    });
  }
  return { items: out, errors };
}

export const GROUP_FULL_TEMPLATE_HEADERS = [
  '分组名*',
  '公司*',
  '邮箱*',
  '姓名*',
  '角色标签（选填）',
  '备注（选填）',
];
export const GROUP_FULL_TEMPLATE_SAMPLE = [
  ['驰云', '驰云车联', 'zhang@chiyun.com', '张三', '采购', ''],
  ['驰云', '驰云车联', 'li@chiyun.com', '李四', '品管', ''],
];

// ── 成员批量导入（按邮箱匹配已有账号 + 角色）──
const MEMBER_IMPORT_FIELD_MAP: Record<string, string> = {
  邮箱: 'email',
  邮件: 'email',
  email: 'email',
  角色: 'role',
  权限: 'role',
  role: 'role',
};

function normRole(v?: string): 'admin' | 'editor' | 'viewer' | undefined {
  const s = String(v ?? '')
    .trim()
    .toLowerCase();
  if (['admin', '管理员', '管理員'].includes(s)) return 'admin';
  if (['editor', '编辑', '編輯'].includes(s)) return 'editor';
  if (['viewer', '审核员', '審核員', '审核', '審核'].includes(s)) return 'viewer';
  return undefined;
}

export interface MemberImportRow {
  email: string;
  role: 'admin' | 'editor' | 'viewer';
}

export function parseMemberSheet(
  wb: XLSX.WorkBook,
  sheetName: string,
): { items: MemberImportRow[]; errors: { rowIndex: number; reason: string }[] } {
  const { items, errors } = parseKeyedSheet(wb, sheetName, MEMBER_IMPORT_FIELD_MAP);
  const out: MemberImportRow[] = [];
  for (const it of items) {
    const rowNo = Number(it['__row']);
    const email = (it['email'] ?? '').toLowerCase();
    if (!EMAIL_RE.test(email)) {
      errors.push({ rowIndex: rowNo, reason: `邮箱无效：${it['email'] ?? '(空)'}` });
      continue;
    }
    const role = normRole(it['role']);
    if (!role) {
      errors.push({ rowIndex: rowNo, reason: `角色无效（应为 管理员/编辑/审核员）：${email}` });
      continue;
    }
    out.push({ email, role });
  }
  return { items: out, errors };
}

export const MEMBER_TEMPLATE_HEADERS = ['邮箱*', '角色*（管理员/编辑/审核员）'];
export const MEMBER_TEMPLATE_SAMPLE = [
  ['zhang@company.com', '管理员'],
  ['li@company.com', '编辑'],
];

// ── 用户批量导入（平台管理员）──
const USER_IMPORT_FIELD_MAP: Record<string, string> = {
  邮箱: 'email',
  邮件: 'email',
  email: 'email',
  姓名: 'name',
  名称: 'name',
  name: 'name',
  初始密码: 'initial_password',
  密码: 'initial_password',
  password: 'initial_password',
  平台管理员: 'is_platform_admin',
  管理员: 'is_platform_admin',
};

function toBool(v?: string): boolean {
  const s = String(v ?? '')
    .trim()
    .toLowerCase();
  return ['是', 'y', 'yes', 'true', '1', '√'].includes(s);
}

export interface UserImportRow {
  email: string;
  name: string;
  initial_password: string;
  is_platform_admin: boolean;
}

export function parseUserSheet(
  wb: XLSX.WorkBook,
  sheetName: string,
): { items: UserImportRow[]; errors: { rowIndex: number; reason: string }[] } {
  const { items, errors } = parseKeyedSheet(wb, sheetName, USER_IMPORT_FIELD_MAP);
  const out: UserImportRow[] = [];
  for (const it of items) {
    const rowNo = Number(it['__row']);
    const email = (it['email'] ?? '').toLowerCase();
    if (!EMAIL_RE.test(email)) {
      errors.push({ rowIndex: rowNo, reason: `邮箱无效：${it['email'] ?? '(空)'}` });
      continue;
    }
    if (!it['name']) {
      errors.push({ rowIndex: rowNo, reason: `缺少姓名（${email}）` });
      continue;
    }
    const pwd = it['initial_password'] ?? '';
    if (pwd.length < PASSWORD_MIN_LENGTH) {
      errors.push({
        rowIndex: rowNo,
        reason: `初始密码至少 ${PASSWORD_MIN_LENGTH} 位（${email}）`,
      });
      continue;
    }
    out.push({
      email,
      name: it['name'],
      initial_password: pwd,
      is_platform_admin: toBool(it['is_platform_admin']),
    });
  }
  return { items: out, errors };
}

export const USER_TEMPLATE_HEADERS = ['邮箱*', '姓名*', '初始密码*（≥8位）', '平台管理员（是/否）'];
export const USER_TEMPLATE_SAMPLE = [
  ['zhang@company.com', '张三', 'Init@2026', '否'],
  ['admin2@company.com', '李四', 'Init@2026', '是'],
];

// ── 产品「整表导入」：一张表含产品 + 成员（按编码自动建/复用产品）──
const TENANT_FULL_IMPORT_FIELD_MAP: Record<string, string> = {
  编码: 'code',
  編碼: 'code',
  code: 'code',
  名称: 'name',
  名字: 'name',
  name: 'name',
  客户公司: 'customer_company',
  客戶公司: 'customer_company',
  生产工厂: 'customer_company',
  生產工廠: 'customer_company',
  工厂: 'customer_company',
  公司: 'customer_company',
  company: 'customer_company',
  factory: 'customer_company',
  成员邮箱: 'member_email',
  成員郵箱: 'member_email',
  成员: 'member_email',
  member: 'member_email',
  成员角色: 'member_role',
  成員角色: 'member_role',
  角色: 'member_role',
  role: 'member_role',
};

export interface TenantFullImportRow {
  code: string;
  name: string;
  customer_company?: string;
  member_email?: string;
  member_role?: 'admin' | 'editor' | 'viewer';
}

/** 解析产品导入表；每行 = 产品（编码/名称/客户公司）+ 可选一名成员（邮箱/角色）。同一产品多成员写多行。 */
export function parseTenantFullSheet(
  wb: XLSX.WorkBook,
  sheetName: string,
): { items: TenantFullImportRow[]; errors: { rowIndex: number; reason: string }[] } {
  const { items, errors } = parseKeyedSheet(wb, sheetName, TENANT_FULL_IMPORT_FIELD_MAP, {
    field: 'code',
    label: '编码',
  });
  const out: TenantFullImportRow[] = [];
  for (const it of items) {
    const rowNo = Number(it['__row']);
    if (!it['code']) {
      errors.push({ rowIndex: rowNo, reason: '缺少编码' });
      continue;
    }
    if (!it['name']) {
      errors.push({ rowIndex: rowNo, reason: `缺少名称（${it['code']}）` });
      continue;
    }
    const row: TenantFullImportRow = {
      code: it['code'],
      name: it['name'],
      ...(it['customer_company'] ? { customer_company: it['customer_company'] } : {}),
    };
    const email = (it['member_email'] ?? '').toLowerCase();
    if (email) {
      if (!EMAIL_RE.test(email)) {
        errors.push({ rowIndex: rowNo, reason: `成员邮箱无效：${it['member_email']}` });
        continue;
      }
      const role = normRole(it['member_role']);
      if (!role) {
        errors.push({
          rowIndex: rowNo,
          reason: `成员角色无效（应为 管理员/编辑/审核员）：${email}`,
        });
        continue;
      }
      row.member_email = email;
      row.member_role = role;
    }
    out.push(row);
  }
  return { items: out, errors };
}

export const TENANT_FULL_TEMPLATE_HEADERS = [
  '编码*',
  '名称*',
  '生产工厂（选填）',
  '成员邮箱（选填）',
  '成员角色（管理员/编辑/审核员）',
];
export const TENANT_FULL_TEMPLATE_SAMPLE = [
  ['K8S-PROJECT-A', 'S1 Ultra', '某某科技', 'zhang@company.com', '管理员'],
  ['K8S-PROJECT-A', 'S1 Ultra', '某某科技', 'li@company.com', '编辑'],
  ['K8S-PROJECT-B', 'X2 Pro', '另一公司', '', ''],
];

/** 显示宽度：CJK 全角字符按 2 计（用于自动列宽，避免长 UUID/中文被截断） */
function displayWidth(v: unknown): number {
  const s = String(v ?? '');
  let w = 0;
  for (const ch of s) w += ch.charCodeAt(0) > 255 ? 2 : 1;
  return w;
}

/** 通用：把二维数据（表头 + 行）导出为 .xlsx，自动列宽，浏览器直接存档 */
export function exportRowsToXlsx(
  filename: string,
  headers: string[],
  rows: (string | number)[][],
): void {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  // 按内容自动设列宽（1~60 字符），避免 UUID / JSON 被视觉截断
  ws['!cols'] = headers.map((h, i) => {
    let max = displayWidth(h);
    for (const r of rows) max = Math.max(max, displayWidth(r[i]));
    return { wch: Math.min(60, Math.max(8, max + 2)) };
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, `${filename}.xlsx`);
}
